import {
  Autocomplete,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Paper,
  Stack,
  TextField,
  Typography
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import LocationOnOutlinedIcon from "@mui/icons-material/LocationOnOutlined";
import WorkIcon from "@mui/icons-material/Work";
import PersonIcon from "@mui/icons-material/Person";
import CategoryIcon from "@mui/icons-material/Category";
import ClassIcon from "@mui/icons-material/Class";
import FlagIcon from "@mui/icons-material/Flag";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import { BIBlue, BILightGrey, BIRed } from "../../../assets/helpers";
import { useEffect, useState } from "react";

const filterOptions = [
  {
    key: "location",
    label: "Location",
    description: "Search by Place",
    placeholder: "Location",
    icon: LocationOnOutlinedIcon
  },
  {
    key: "job",
    label: "Job",
    description: "Search by Job Details",
    placeholder: "Job code or name",
    icon: WorkIcon
  },
  {
    key: "employee",
    label: "Employee",
    description: "Search by Employee Details",
    placeholder: "Employee name",
    icon: PersonIcon
  },
  {
    key: "category",
    label: "Category",
    description: "Search by Asset Category",
    placeholder: "Select Category",
    icon: CategoryIcon
  },
  {
    key: "subCategory",
    label: "Sub Category",
    description: "Search by Asset Sub Category",
    placeholder: "Select Sub Category",
    icon: ClassIcon
  },
  {
    key: "status",
    label: "Status",
    description: "Search by Asset Status",
    placeholder: "Select Status",
    icon: FlagIcon
  }
];

const AssetFilterModal = ({
  filterModalOpen,
  setFilterModalOpen,
  draftFilters = {},
  setDraftFilters = () => {},
  jobOptions = [],
  jobLoading = false,
  employeeOptions = [],
  employeeLoading = false,
  categoryOptions = [],
  categoryLoading = false,
  onCategorySearch = () => {},
  subCategoryOptions = [],
  subCategoryLoading = false,
  onSubCategorySearch = () => {},
  onSubCategoryFetchImmediate = () => {},
  statusOptions = [],
  statusLoading = false,
  onStatusSearch = () => {},
  handleClearAllFilters = () => {},
  handleFilterApply = () => {},
  selectedFilterOption,
  setSelectedFilterOption = () => {},
  accentColor,
  canViewAllAssets,
  empID,
  onJobSearch = () => {},
  onEmployeeSearch = () => {}
}) => {
  const shouldRestrictToSelf = !canViewAllAssets;

  // Track expanded cards (default to only active filters, or none if empty)
  const getInitialExpandedKeys = () => {
    const initial = [];
    if (draftFilters.location) initial.push("location");
    if (draftFilters.job || draftFilters.jobId) initial.push("job");
    if (draftFilters.employee || draftFilters.employeeId) initial.push("employee");
    if (draftFilters.category || draftFilters.categoryId) initial.push("category");
    if (draftFilters.subCategory || draftFilters.subCategoryId) initial.push("subCategory");
    if (draftFilters.status || draftFilters.statusId) initial.push("status");
    return Array.from(new Set(initial));
  };

  const [expandedKeys, setExpandedKeys] = useState(getInitialExpandedKeys);

  useEffect(() => {
    if (filterModalOpen) {
      setExpandedKeys(getInitialExpandedKeys());
    }
  }, [filterModalOpen]);

  useEffect(() => {
    if (shouldRestrictToSelf && empID) {
      setDraftFilters((prev) => ({
        ...prev,
        employeeId: empID
      }));
    }
  }, [shouldRestrictToSelf, empID, setDraftFilters]);

  const accent = accentColor ?? BIBlue;
  const selectedBg = accent ? `${accent}14` : "#f0f4ff";

  const handleFilterChange = (field) => (event) => {
    setDraftFilters((prev) => ({ ...prev, [field]: event.target.value }));
  };

  const toggleCard = (key) => {
    setExpandedKeys((prev) => {
      const exists = prev.includes(key);
      const next = exists ? prev.filter((k) => k !== key) : [...prev, key];
      return next;
    });
    if (key === "subCategory" && subCategoryOptions.length === 0) {
      onSubCategoryFetchImmediate(draftFilters.categoryId || "", "");
    }
  };

  const handleClearSingleFilter = (key) => {
    setDraftFilters((prev) => {
      const next = { ...prev };
      if (key === "location") next.location = "";
      if (key === "job") {
        next.job = "";
        next.jobId = "";
      }
      if (key === "employee") {
        next.employee = "";
        next.employeeId = "";
      }
      if (key === "category") {
        next.category = "";
        next.categoryId = "";
        next.subCategory = "";
        next.subCategoryId = "";
      }
      if (key === "subCategory") {
        next.subCategory = "";
        next.subCategoryId = "";
      }
      if (key === "status") {
        next.status = "";
        next.statusId = "";
      }
      return next;
    });
  };

  const getFilterValue = (key) => {
    if (key === "location") return draftFilters.location || "";
    if (key === "job") return draftFilters.job || "";
    if (key === "employee") return draftFilters.employee || "";
    if (key === "category") return draftFilters.category || "";
    if (key === "subCategory") return draftFilters.subCategory || "";
    if (key === "status") return draftFilters.status || "";
    return "";
  };

  return (
    <Dialog
      fullWidth
      maxWidth="sm"
      open={filterModalOpen}
      onClose={() => setFilterModalOpen(false)}>
      <DialogTitle sx={{ m: 0, p: 2 }}>
        <Stack direction="row" sx={{ justifyContent: "space-between", alignItems: "center" }}>
          <Typography variant="h6" sx={{ fontWeight: 600 }}>Filter Assets</Typography>
          <IconButton onClick={() => setFilterModalOpen(false)} size="small">
            <CloseIcon />
          </IconButton>
        </Stack>
      </DialogTitle>
      <DialogContent dividers sx={{ p: 2.5 }}>
        <Stack spacing={2}>
          {filterOptions.map((option) => {
            const isExpanded = expandedKeys.includes(option.key);
            const val = getFilterValue(option.key);
            const hasValue = Boolean(val);

            return (
              <Paper
                key={option.key}
                elevation={0}
                sx={{
                  p: 2,
                  borderRadius: 2,
                  border: hasValue ? `1.5px solid ${accent}` : "1px solid rgba(0,0,0,0.1)",
                  backgroundColor: hasValue ? selectedBg : "#fff",
                  transition: "all 0.2s ease"
                }}>
                <Stack
                  sx={{ justifyContent: "space-between", alignItems: "center", cursor: "pointer" }}
                  direction="row"
                  onClick={() => toggleCard(option.key)}>
                  <Stack direction="row" sx={{ alignItems: "center" }} spacing={1.5}>
                    <Box
                      sx={{
                        width: 38,
                        height: 38,
                        borderRadius: 2,
                        bgcolor: hasValue ? `${accent}25` : BILightGrey,
                        color: hasValue ? accent : "inherit",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center"
                      }}>
                      <option.icon fontSize="small" />
                    </Box>
                    <Box>
                      <Typography variant="subtitle1" sx={{ fontWeight: hasValue ? 600 : 500, lineHeight: 1.2 }}>
                        {option.label}
                      </Typography>
                      <Typography variant="body2" sx={{ color: hasValue ? accent : "text.secondary", fontSize: "0.82rem" }}>
                        {hasValue ? `Selected: ${val}` : option.description}
                      </Typography>
                    </Box>
                  </Stack>
                  <Stack direction="row" spacing={1} sx={{ alignItems: "center" }}>
                    {hasValue && (
                      <Chip
                        label={val}
                        size="small"
                        onDelete={(e) => {
                          e.stopPropagation();
                          handleClearSingleFilter(option.key);
                        }}
                        sx={{
                          bgcolor: accent,
                          color: "#fff",
                          fontWeight: 500,
                          maxWidth: 160,
                          "& .MuiChip-deleteIcon": { color: "#fff" }
                        }}
                      />
                    )}
                    <IconButton size="small" sx={{ color: "text.secondary" }}>
                      {isExpanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                    </IconButton>
                  </Stack>
                </Stack>
                {isExpanded && (
                  <Box sx={{ mt: 1.5, pt: 1, borderTop: "1px dashed rgba(0,0,0,0.08)" }}>
                    {option.key === "location" && (
                      <TextField
                        fullWidth
                        size="small"
                        label={option.label}
                        placeholder={option.placeholder}
                        value={draftFilters.location || ""}
                        onChange={handleFilterChange("location")}
                      />
                    )}
                    {option.key === "job" && (
                      <Autocomplete
                        freeSolo
                        size="small"
                        options={jobOptions}
                        inputValue={draftFilters.job || ""}
                        getOptionLabel={(option) =>
                          typeof option === "string" ? option : (option?.label ?? "")
                        }
                        onInputChange={(_, value) => {
                          setDraftFilters((prev) => ({ ...prev, job: value, jobId: "" }));
                          onJobSearch(value);
                        }}
                        onChange={(_, option) => {
                          const label = typeof option === "string" ? option : (option?.label ?? "");
                          const id = typeof option === "object" ? (option?.id ?? "") : "";
                          setDraftFilters((prev) => ({ ...prev, job: label, jobId: id }));
                        }}
                        loading={jobLoading}
                        value={null}
                        renderInput={(params) => (
                          <TextField
                            {...params}
                            label={option.label}
                            placeholder={option.placeholder}
                            InputProps={{
                              ...params.InputProps,
                              endAdornment: (
                                <>
                                  {jobLoading ? (
                                    <CircularProgress color="inherit" size={20} />
                                  ) : null}
                                  {params.InputProps?.endAdornment}
                                </>
                              )
                            }}
                          />
                        )}
                      />
                    )}
                    {option.key === "employee" && (
                      <Autocomplete
                        freeSolo
                        size="small"
                        options={employeeOptions}
                        inputValue={draftFilters.employee || ""}
                        disabled={shouldRestrictToSelf}
                        getOptionLabel={(option) =>
                          typeof option === "string" ? option : (option?.label ?? "")
                        }
                        onInputChange={(_, value) => {
                          setDraftFilters((prev) => ({ ...prev, employee: value, employeeId: "" }));
                          onEmployeeSearch(value);
                        }}
                        onChange={(_, option) => {
                          const label = typeof option === "string" ? option : (option?.label ?? "");
                          const id = typeof option === "object" ? (option?.id ?? "") : "";
                          setDraftFilters((prev) => ({
                            ...prev,
                            employee: label,
                            employeeId: id
                          }));
                        }}
                        loading={employeeLoading}
                        value={
                          shouldRestrictToSelf
                            ? employeeOptions.find((emp) => emp.id === empID) || null
                            : null
                        }
                        renderInput={(params) => (
                          <TextField
                            {...params}
                            label={option.label}
                            placeholder={option.placeholder}
                            InputProps={{
                              ...params.InputProps,
                              endAdornment: (
                                <>
                                  {employeeLoading ? (
                                    <CircularProgress color="inherit" size={20} />
                                  ) : null}
                                  {params.InputProps?.endAdornment}
                                </>
                              )
                            }}
                          />
                        )}
                      />
                    )}
                    {option.key === "category" && (
                      <Autocomplete
                        size="small"
                        options={categoryOptions}
                        getOptionLabel={(option) =>
                          typeof option === "string" ? option : (option?.label ?? "")
                        }
                        value={
                          categoryOptions.find((c) => String(c.id) === String(draftFilters.categoryId)) ||
                          (draftFilters.category ? { id: draftFilters.categoryId, label: draftFilters.category } : null)
                        }
                        onInputChange={(_, value, reason) => {
                          if (reason === "input") {
                            onCategorySearch(value);
                          }
                        }}
                        onChange={(_, option) => {
                          const label = typeof option === "string" ? option : (option?.label ?? "");
                          const id = typeof option === "object" && option ? (option?.id ?? "") : "";
                          setDraftFilters((prev) => ({
                            ...prev,
                            category: label,
                            categoryId: id,
                            // Clear subcategory when category changes
                            subCategory: "",
                            subCategoryId: ""
                          }));
                          onSubCategoryFetchImmediate(id, "");
                          // Auto-expand subcategory card for smooth cascading
                          if (id) {
                            setExpandedKeys((prev) => prev.includes("subCategory") ? prev : [...prev, "subCategory"]);
                          }
                        }}
                        loading={categoryLoading}
                        renderInput={(params) => (
                          <TextField
                            {...params}
                            label={option.label}
                            placeholder={option.placeholder}
                            InputProps={{
                              ...params.InputProps,
                              endAdornment: (
                                <>
                                  {categoryLoading ? (
                                    <CircularProgress color="inherit" size={20} />
                                  ) : null}
                                  {params.InputProps?.endAdornment}
                                </>
                              )
                            }}
                          />
                        )}
                      />
                    )}
                    {option.key === "subCategory" && (
                      <Autocomplete
                        size="small"
                        options={subCategoryOptions}
                        getOptionLabel={(option) =>
                          typeof option === "string" ? option : (option?.label ?? "")
                        }
                        value={
                          subCategoryOptions.find((sc) => String(sc.id) === String(draftFilters.subCategoryId)) ||
                          (draftFilters.subCategory ? { id: draftFilters.subCategoryId, label: draftFilters.subCategory } : null)
                        }
                        onInputChange={(_, value, reason) => {
                          if (reason === "input") {
                            onSubCategorySearch(draftFilters.categoryId, value);
                          }
                        }}
                        onChange={(_, option) => {
                          const label = typeof option === "string" ? option : (option?.label ?? "");
                          const id = typeof option === "object" && option ? (option?.id ?? "") : "";
                          setDraftFilters((prev) => ({
                            ...prev,
                            subCategory: label,
                            subCategoryId: id
                          }));
                        }}
                        loading={subCategoryLoading}
                        renderInput={(params) => (
                          <TextField
                            {...params}
                            label={option.label}
                            placeholder={
                              !draftFilters.categoryId
                                ? "Select Sub Category (or pick a Category first)"
                                : option.placeholder
                            }
                            helperText={
                              draftFilters.category
                                ? `Subcategories for "${draftFilters.category}"`
                                : "Tip: You can select a Category above to narrow subcategories"
                            }
                            InputProps={{
                              ...params.InputProps,
                              endAdornment: (
                                <>
                                  {subCategoryLoading ? (
                                    <CircularProgress color="inherit" size={20} />
                                  ) : null}
                                  {params.InputProps?.endAdornment}
                                </>
                              )
                            }}
                          />
                        )}
                      />
                    )}
                    {option.key === "status" && (
                      <Autocomplete
                        size="small"
                        options={statusOptions}
                        getOptionLabel={(option) =>
                          typeof option === "string" ? option : (option?.label ?? "")
                        }
                        value={
                          statusOptions.find((s) => String(s.id) === String(draftFilters.statusId)) ||
                          (draftFilters.status ? { id: draftFilters.statusId, label: draftFilters.status } : null)
                        }
                        onInputChange={(_, value, reason) => {
                          if (reason === "input") {
                            onStatusSearch(value);
                          }
                        }}
                        onChange={(_, option) => {
                          const label = typeof option === "string" ? option : (option?.label ?? "");
                          const id = typeof option === "object" && option ? (option?.id ?? "") : "";
                          setDraftFilters((prev) => ({
                            ...prev,
                            status: label,
                            statusId: id
                          }));
                        }}
                        loading={statusLoading}
                        renderInput={(params) => (
                          <TextField
                            {...params}
                            label={option.label}
                            placeholder={option.placeholder}
                            InputProps={{
                              ...params.InputProps,
                              endAdornment: (
                                <>
                                  {statusLoading ? (
                                    <CircularProgress color="inherit" size={20} />
                                  ) : null}
                                  {params.InputProps?.endAdornment}
                                </>
                              )
                            }}
                          />
                        )}
                      />
                    )}
                  </Box>
                )}
              </Paper>
            );
          })}
        </Stack>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button
          onClick={() => setFilterModalOpen(false)}
          sx={{ color: "text.secondary", textTransform: "none" }}>
          Cancel
        </Button>
        <Button onClick={handleClearAllFilters} sx={{ color: BIRed, textTransform: "none" }}>
          Clear All
        </Button>
        <Button
          variant="contained"
          onClick={handleFilterApply}
          sx={{
            backgroundColor: accent,
            color: "#fff",
            "&:hover": {
              backgroundColor: accent
            },
            textTransform: "none",
            borderRadius: 2,
            px: 3
          }}>
          Apply Filters
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default AssetFilterModal;
